import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    LinearProgress,
    Alert
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import toast from 'react-hot-toast';

// Sample TSV offered for download so users can see the expected format.
const SAMPLE_TSV_FILENAME = 'SAMPLE_FOLDER_STRUCTURE.tsv';
const SAMPLE_TSV_CONTENT = [
    ['1', 'Development'],
    ['1.1', 'Frontend'],
    ['1.1.1', 'Components'],
    ['1.1.2', 'Styles'],
    ['1.2', 'Backend'],
    ['1.2.1', 'APIs'],
    ['1.2.2', 'Database'],
    ['2', 'Marketing'],
    ['2.1', 'Social Media'],
    ['2.2', 'Email Campaign'],
    ['2.2.1', 'Templates'],
    ['3', 'Operations'],
    ['3.1', 'IT Support'],
    ['3.2', 'HR Tasks'],
].map((row) => row.join('\t')).join('\n');

const TsvUploadDialog = ({ open, onClose, onUpload }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const handleFileSelect = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            if (!selectedFile.name.endsWith('.tsv')) {
                toast.error('Please select a TSV file');
                return;
            }
            setFile(selectedFile);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleDownloadSample = () => {
        const blob = new Blob([SAMPLE_TSV_CONTENT], { type: 'text/tab-separated-values' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = SAMPLE_TSV_FILENAME;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setDragOver(false);
        const droppedFile = event.dataTransfer.files[0];
        if (droppedFile) {
            if (!droppedFile.name.endsWith('.tsv')) {
                toast.error('Please select a TSV file');
                return;
            }
            setFile(droppedFile);
        }
    };

    // Maximum nesting depth (root = level 1) that a TSV may create.
    const MAX_DEPTH = 10;

    // A valid hierarchy number is dot-separated integers, e.g. "1", "2.3", "1.1.1"
    const isHierarchyNumber = (value) => /^\d+(\.\d+)*$/.test(value);

    // Depth = how many levels deep a number sits, e.g. "1" -> 1, "1.2.3" -> 3.
    const getDepth = (number) => number.split('.').length;

    // The parent number is the same path with the last segment removed.
    // Top-level numbers (e.g. "1") have no parent -> null.
    const getParentNumber = (number) => {
        const lastDot = number.lastIndexOf('.');
        return lastDot === -1 ? null : number.slice(0, lastDot);
    };

    // Read the file into data rows: { number, name, line }. Only fully blank lines
    // are ignored; every other line is expected to be a real "<number><tab><name>"
    // row and is validated below. Line numbers are kept (1-based) so validation can
    // point at the exact row.
    const parseRows = (fileContent) => {
        const rows = [];
        fileContent.split('\n').forEach((line, index) => {
            if (line.trim() === '') return;

            const columns = line.split('\t');
            rows.push({
                number: columns[0].trim(),
                name: columns.slice(1).join('\t').trim(),
                line: index + 1
            });
        });
        return rows;
    };

    // Each validator inspects the rows and returns an error message, or null if OK.
    const findInvalidNumberError = (rows) => {
        const invalid = rows.find((row) => !isHierarchyNumber(row.number));
        if (!invalid) return null;

        // Common mistake: the number and name are separated by a space instead of a
        // TAB, so the whole "1.1 test" lands in the number column. Call it out.
        if (/^\d+(\.\d+)*\s/.test(invalid.number)) {
            return `Row ${invalid.line}: "${invalid.number}" — use a TAB (not a space) between the number and the folder name.`;
        }

        return `Row ${invalid.line}: "${invalid.number}" is not a valid hierarchy number. Each row must start with a number like 1, 1.1 or 2.3 (no header row).`;
    };

    const findMissingNameError = (rows) => {
        const nameless = rows.find((row) => !row.name);
        return nameless
            ? `Row ${nameless.line}: "${nameless.number}" has no folder name. Use: <number><tab><folder name>.`
            : null;
    };

    const findDuplicateError = (rows) => {
        const seen = new Map();
        for (const { number, line } of rows) {
            if (seen.has(number)) {
                return `Duplicate number "${number}" on rows ${seen.get(number)} and ${line}. Each hierarchy number must appear only once.`;
            }
            seen.set(number, line);
        }
        return null;
    };

    const findDepthError = (rows) => {
        const tooDeep = rows.find((row) => getDepth(row.number) > MAX_DEPTH);
        return tooDeep
            ? `Maximum nesting depth is ${MAX_DEPTH} levels. "${tooDeep.number}" (row ${tooDeep.line}) goes too deep.`
            : null;
    };

    const findMissingParentError = (rows) => {
        const numbers = new Set(rows.map((row) => row.number));
        for (const { number, line } of rows) {
            const parent = getParentNumber(number);
            if (parent !== null && !numbers.has(parent)) {
                return `"${number}" (row ${line}) has no parent. Add its parent folder "${parent}" before "${number}".`;
            }
        }
        return null;
    };

    // Order matters: the number format is checked first, since the later
    // validators rely on parsing depth / parent from a valid hierarchy number.
    const VALIDATORS = [
        findInvalidNumberError,
        findMissingNameError,
        findDuplicateError,
        findDepthError,
        findMissingParentError
    ];

    const parseTsvFile = (fileContent) => {
        const rows = parseRows(fileContent);

        if (rows.length === 0) {
            throw new Error('No valid rows found. Each row must be: <number><tab><folder name>');
        }

        // Run every validator up front so nothing is created from a malformed file.
        for (const validate of VALIDATORS) {
            const error = validate(rows);
            if (error) throw new Error(error);
        }

        // Sort shallow-to-deep so every parent is created before its children.
        const sorted = [...rows].sort((a, b) => getDepth(a.number) - getDepth(b.number));

        return {
            folders: sorted.map((row) => ({
                number: row.number,
                name: row.name,
                parentNumber: getParentNumber(row.number)
            }))
        };
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error('Please select a file first');
            return;
        }

        setUploading(true);

        try {
            const fileContent = await file.text();
            const structure = parseTsvFile(fileContent);
            
            // Pass the structure to parent component for folder creation
            await onUpload(structure);
            
            toast.success('Folder structure created successfully!');
            setFile(null);
            onClose();
        } catch (error) {
            console.error('Error processing TSV file:', error);
            toast.error(error.message || 'Error processing TSV file');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        if (!uploading) {
            setFile(null);
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Upload TSV to Create Folder Structure</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>How it works:</strong>
                        </Typography>
                        <Typography variant="body2" component="div">
                            • Each row is: <strong>number</strong> &nbsp;⇥&nbsp; <strong>folder name</strong> (tab-separated)<br />
                            • The number sets the hierarchy: <code>1</code> is a root folder<br />
                            • <code>1.1</code> is a child of <code>1</code>, <code>1.1.1</code> a child of <code>1.1</code><br />
                            • <code>2</code> is another root, <code>2.1</code> its child, and so on (up to {MAX_DEPTH} levels)
                        </Typography>
                    </Alert>

                    <Button
                        onClick={handleDownloadSample}
                        startIcon={<DownloadIcon />}
                        size="small"
                        sx={{ textTransform: 'none', color: '#47b6af' }}
                    >
                        Download sample TSV
                    </Button>
                </Box>

                <Box
                    sx={{
                        border: dragOver ? '2px dashed #47b6af' : '2px dashed #ccc',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: dragOver ? 'rgba(71, 182, 175, 0.1)' : '#fafafa',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: 'rgba(71, 182, 175, 0.05)',
                            borderColor: '#47b6af'
                        }
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('tsv-file-input').click()}
                >
                    <input
                        id="tsv-file-input"
                        type="file"
                        accept=".tsv"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    <UploadFileIcon sx={{ fontSize: 48, color: '#47b6af', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        {file ? file.name : 'Click or drag TSV file here'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Only .tsv files are accepted
                    </Typography>
                </Box>

                {uploading && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress />
                        <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                            Creating folder structure...
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleUpload} 
                    variant="contained" 
                    disabled={!file || uploading}
                    sx={{
                        backgroundColor: '#47b6af',
                        '&:hover': {
                            backgroundColor: '#3a9a94'
                        }
                    }}
                >
                    Create Folders
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TsvUploadDialog;

